import React, { useState, useCallback, useRef, useEffect } from "react";
import "./ToastContainer.css";

/**
 * Minimal toast notification system.
 *
 * Usage:
 *   const toastRef = useRef();
 *   <ToastContainer ref={toastRef} />
 *   toastRef.current.show("Sauvegardé !", "success");
 *
 * Types: "success" | "error" | "info" | "warning"
 */

let _idCounter = 0;
const AUTO_DISMISS_MS = 4000;

const ToastContainer = React.forwardRef(function ToastContainer(_props, ref) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  // Expose .show() and .dismiss() to parent via ref
  React.useImperativeHandle(ref, () => ({
    show(message, type = "info") {
      const id = ++_idCounter;
      setToasts((prev) => [...prev, { id, message, type }]);

      timersRef.current[id] = setTimeout(() => {
        dismiss(id);
      }, AUTO_DISMISS_MS);

      return id;
    },
    dismiss(id) {
      dismiss(id);
    },
  }));

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  if (toasts.length === 0) return null;

  const ICONS = {
    success: "check_circle",
    error: "error",
    warning: "warning",
    info: "info",
  };

  return (
    <div className="toast-container" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast-${toast.type}`}>
          <span className="material-symbols-rounded toast-icon">
            {ICONS[toast.type] || "info"}
          </span>
          <span className="toast-message">{toast.message}</span>
          <button
            type="button"
            className="toast-close"
            onClick={() => dismiss(toast.id)}
            aria-label="Fermer"
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>
      ))}
    </div>
  );
});

export default ToastContainer;
